// @ts-strict-ignore
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import {AttrsSchema, Component} from 'panel';

import {
  DATASET_PROPERTY_NAME,
  MappedPropertyValues,
  PropertyType,
  ResourceType,
  SCD_PROFILE_PROPERTY_PREFIX,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {filterAttributes, getOwnAttr, parseNumber} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {peoplePropertyValuesClient} from 'common/report/queries/query-api/people-property-values-client';
import {NAVIGATION_KEY_CODES, propagateEvent, rafRetry} from 'common/util/dom';
import {
  ConstantPropertyValueInfo,
  ConstantPropertyValues,
  ConstantPropertyValueSet,
  ConstantPropertyValuesInfo,
  getIconForPropertyType,
  getLabelForPropertyType,
  isScdPropertyName,
  propertiesUtils,
  renamePropertyValue,
} from 'common/report/util/properties';
import {getPropertyValueMappings, syncReportPropertyValueMappings} from 'common/report/util/mappings';
import {truncateMiddle} from 'common/util/string';
import {SelectionState} from 'common/components/items-menu/constants';
import {
  FLOWS_CONVERSION,
  FLOWS_CONVERSION_VALUES,
  RECURRING_REVENUE_CHANGE_TYPE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {SpecialTimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DAYS_OF_THE_WEEK, HOURS_OF_DAY, MPProperties} from 'common/constants';
import {LabelTheme, Section, SectionItem} from 'common/components/items-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {getPropertyType} from 'common/query-builder/query-entry-section/row-types/row-util';
import {getBorrowedProperty} from 'common/widgets/custom-property-modal/util';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {PropertyValue} from 'common/query-builder/query-block/properties-menu/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {REVENUE_CHANGE_TYPES} from 'common/query-builder/query-entry-section/components/revenue-change/constants';

import {DATE_RANGE_DISPLAY_CONTEXT_MAP, PropertyFilterFactory} from '../models/utils';
import {isEnhancedListFilter} from '../utils';
import {
  DisplayContext as PropertyFilterDisplayContext,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from '../models/types';
import type {PropertyFilter} from '../models/property-filter';
import {template} from './template';
import css from './index.inline.styl';

interface PropertyValuesScreenState {
  loadingPropertyValues: boolean;
  datasets?: any;
  editingPropertyType: boolean;
  propertyFilter?: PropertyFilter;
  propertyValues?: string[];
  searchFilter: string;
  currentValues?: any[];
}

interface PropertyValuesScreenParams {
  listProperty?: Segmentation;
}

const MAX_SPECIFY_VALUE_LENGTH = 255;

export class PropertyValuesScreen extends Component<
  PropertyValuesScreenState,
  any,
  unknown,
  unknown,
  IronContextRegistry,
  PropertyValuesScreenParams
> {
  private declare keydownProxyEl: HTMLElement;
  private handleKeyPress = null;

  static get attrsSchema(): AttrsSchema<any> {
    return {
      ...super.attrsSchema,
      'display-context': {type: `string`, default: PropertyFilterDisplayContext.Default},
      'enable-enhanced-list-filters': `boolean`,
      'event-name': {
        type: `string`,
        default: null,
        description: `The name of the event, if this is an event property, It will be used to fetch the list of property values.`,
      },
      'event-names': {
        type: `json`,
        default: null,
        description: `Use instead of "event-name" if there are multiple events.`,
      },
      'disable-multi-select': `boolean`,
      'disable-specify': `boolean`,
      'display-value-only': `boolean`,
      'hide-undefined-value': {
        type: `boolean`,
        description: `Do not show "undefined" value`,
      },
      'has-back-button': `boolean`,
      'project-id': `number`,
      'property-filter': `json`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      contexts: [`i18n` as const],
      defaultState: {
        datasets: null,
        editingPropertyType: false,
        loadingPropertyValues: false,
        propertyFilter: null,
        propertyValues: null,
        searchFilter: null,
      },
      params: {
        listProperty: Object,
      },
      helpers: {
        ...super.config.helpers,
        parseNumber,
        filterAttributes,
        truncateMiddle,

        PropertyType,
        NumberOperator,
        StringOperator,
        PropertyFilterDisplayContext,

        // region Attribute accessors

        hasBackButton: () => {
          return this.attr(`has-back-button`);
        },

        getDisplayContext: () => {
          return this.attr(`display-context`);
        },

        canTypeCast: () => !this.isExperimentsPropertyFilter && !this.enhancedListFilterEnabled(),

        enhancedListFilterEnabled: () => this.enhancedListFilterEnabled(),

        // endregion

        // region Template helpers

        getKeydownProxyEl: (): HTMLElement => {
          const inputElement = this.el.querySelector<HTMLElement>(`input, mp-input`);
          return inputElement || this.keydownProxyEl;
        },

        getPropertyTypeIcon: () => {
          return getIconForPropertyType(getPropertyType(this.state.propertyFilter));
        },

        getPropertyLabel: () => {
          const {propertyName, resourceType, dataGroupId, customProperty} = this.state.propertyFilter;
          return propertiesUtils.renameProperty(propertyName, resourceType, {dataGroupId, customProperty});
        },

        getPropertyTypeMenuSections: () => {
          const typeCastTypes = this.state.propertyFilter.getPropertyTypes();
          const {propertyType, propertyDefaultType} = this.state.propertyFilter;
          const types = [
            ...typeCastTypes,
            ...(typeCastTypes.includes(propertyDefaultType) ? [] : [propertyDefaultType]),
          ];

          const items = types.map((typeCastType) => {
            return {
              icon: getIconForPropertyType(typeCastType),
              isSelected: typeCastType === propertyType,
              label: getLabelForPropertyType(typeCastType),
              value: typeCastType,
            };
          });

          return [{items}];
        },

        getListQuantifierSections: () => {
          const listQuantifier = this.state.propertyFilter.listQuantifier;
          const items = [
            {label: `Any`, value: ListQuantifier.Any, isSelected: listQuantifier === ListQuantifier.Any},
            {label: `All`, value: ListQuantifier.All, isSelected: listQuantifier === ListQuantifier.All},
          ];
          return [{items}];
        },

        getDateRangePickerDisplayContext: () => {
          return DATE_RANGE_DISPLAY_CONTEXT_MAP[this.helpers.getDisplayContext()];
        },

        getFilterOperators: () => {
          return this.state.propertyFilter.getFilterOperators();
        },

        focusInput: (vnode) => rafRetry(() => vnode.elm.focus()),

        isLoadingPropertyValues: () => {
          return !this.state.propertyValues;
        },

        renamePropertyValue: (propertyValue) => {
          return renamePropertyValue(propertyValue, this.state.propertyFilter.propertyName);
        },

        sectionsFromInputFn: (searchFilter: string) => {
          if (
            searchFilter &&
            !this.isExperimentsPropertyFilter &&
            searchFilter.length <= MAX_SPECIFY_VALUE_LENGTH &&
            !this.attr(`disable-specify`)
          ) {
            return [
              {
                items: [
                  {
                    value: searchFilter,
                    added: true,
                    label: `Specify: ${searchFilter}`,
                  },
                ],
              },
            ];
          }

          return null;
        },

        getPropertyValueItems: (): SectionItem[] => {
          let {filterValue, propertyName, propertyType, joinPropertyType} = this.state.propertyFilter;
          let propertyObjectKey: string | number | string[] = this.state.propertyFilter.propertyObjectKey;
          let propertyValues = this.state.propertyValues || [];
          const isObject = propertyType === PropertyType.Object;

          if (Array.isArray(propertyObjectKey)) {
            propertyObjectKey = propertyObjectKey[0];
          }

          // object property types define a different selection
          if (
            joinPropertyType === PropertyType.Object &&
            propertyName === MPProperties.Experiments &&
            Array.isArray(this.state.propertyFilter.propertyObjectKey)
          ) {
            return this.getPropertyObjectKeyValues(propertyObjectKey);
          } else if (isObject && !this.isExperimentsPropertyFilter) {
            propertyValues = this.getPropertyObjectKeys();
          } else if (this.isExperimentsPropertyFilter) {
            // $experiments display the name of a given experiment id
            propertyName = MappedPropertyValues.ExperimentId;
            propertyObjectKey = propertyObjectKey.toString();
          }

          return propertyValues
            .filter((propertyValue) => {
              return (
                // There is a case where propertyValue = null. getPropertyObjectKeys() function returns an array [null]
                // when this.state.propertyFilter is null. Therefore, we filter out this case.
                !isNil(propertyValue) &&
                (!this.attr(`hide-undefined-value`) || propertyValue !== ConstantPropertyValues.NotSet)
              );
            })
            .map((propertyValue) => {
              const isSelected =
                (isObject && propertyObjectKey === propertyValue) ||
                (Array.isArray(filterValue) && filterValue.includes(propertyValue));

              const constantPropertyInfo = getOwnAttr<ConstantPropertyValueInfo>(
                ConstantPropertyValuesInfo,
                propertyValue as ConstantPropertyValues,
              );

              return {
                label: renamePropertyValue(propertyValue, propertyName),
                isSelected,
                propertyValue,
                tooltip: constantPropertyInfo?.tooltip,
                labelTheme: constantPropertyInfo ? LabelTheme.Italic : null,
              };
            });
        },

        getPropertyValueSections: (): Section[] => {
          return [
            {
              isLoading: this.state.loadingPropertyValues,
              items: this.helpers.getPropertyValueItems(),
            },
          ];
        },

        /** Fetches items section for values belonging to the selected key */
        getObjectPropertyValueSections: () => {
          const {filterValue, propertyObjectKey} = this.state.propertyFilter;

          if (!propertyObjectKey) {
            return [{items: []}];
          }

          const items = this.getPropertyObjectKeyValues(propertyObjectKey).map((entry) => {
            return {
              ...entry,
              isSelected:
                entry.value === filterValue || (Array.isArray(filterValue) && filterValue.includes(entry.value)),
            };
          });

          return [{items}];
        },

        insertedIconInput: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.focus();
          }),

        // endregion

        // region Template event handlers

        propagateEvent: (ev) => {
          propagateEvent(this, ev);
        },

        dispatchDraw: () => {
          this.dispatchEvent(new CustomEvent(`draw`));
        },

        clickBackButton: () => {
          this.dispatchEvent(new CustomEvent(`clickBack`));
        },

        clickPropertyTypeButton: () => {
          this.update({editingPropertyType: true});
        },

        changePropertyTypesMenu: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({editingPropertyType: false});
          }
        },

        clickPropertyTypesMenuItem: (ev) => {
          this.update({editingPropertyType: false});
          this.changePropertyFilterAttribute(`propertyType`, ev.detail.item.value);
        },

        handleListQuantifierSelect: (ev) => {
          this.changePropertyFilterAttribute(`listQuantifier`, ev.detail.item.value);
        },

        changeFilterOperator: (filterOperator) => {
          this.changePropertyFilterAttribute(`filterOperator`, filterOperator);
        },

        changeFilterValue: (filterValue) => {
          this.changePropertyFilterAttribute(`filterValue`, filterValue);
        },

        toggleFilterItem: (item) => {
          if (this.attr(`disable-multi-select`)) {
            this.helpers.unselectAllPropertyValues();
          }
          this.selectItems([{...item, isSelected: !item.isSelected}]);
        },

        selectAllMatching: ({detail}) => {
          const items = detail.matchingSections.length ? detail.matchingSections[0].items : [];
          const isSelected = !items.every(({isSelected}) => isSelected);

          this.selectItems(items.map((item) => ({...item, isSelected})));
          this.dispatchEvent(new CustomEvent(`multipartFilterSelection`, {detail: {items}}));
        },

        selectAllPropertyValues: ({detail}) => {
          if (detail.selectionState === SelectionState.All) {
            this.helpers.changeFilterValue([]);
          } else {
            this.helpers.changeFilterValue(this.state.propertyValues);
          }
        },

        unselectAllPropertyValues: () => {
          this.helpers.changeFilterValue([]);
        },

        changeSearchFilter: (searchFilter) => {
          this.update({searchFilter});
        },

        selectContainsPropertyValue: (propertyValue) => {
          const propertyFilter = this.state.propertyFilter.setAttribute(`filterValue`, propertyValue);
          this.changePropertyFilter(propertyFilter);
          this.commitPropertyFilter(propertyFilter);
        },

        selectObjectPropertyKey: (ev) => {
          this.changePropertyFilterAttribute(`propertyObjectKey`, ev.detail.item.propertyValue.toString());
        },

        selectObjectPropertyValue: (ev) => {
          const {added, value} = ev.detail.item;
          this.helpers.changeFilterValue(value !== null ? [value] : null);

          // check if item is defined inline
          if (added) {
            this.update({currentValues: [...this.state.currentValues, value]});
          }
        },

        changeDateRange: (ev) => {
          const {dateRange} = ev.detail;
          const propertyFilter = this.state.propertyFilter.setDateRange(dateRange);
          this.changePropertyFilter(propertyFilter);
        },

        clickAddFilter: () => {
          this.commitPropertyFilter(this.state.propertyFilter);
        },

        // endregion
      },
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    this.updateState();

    switch (attr) {
      case `event-name`:
      case `event-names`:
        this.fetchPropertyValues();
        break;
      default:
        break;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateState();

    this.fetchPropertyValues();

    this.handleKeyPress = (ev) => {
      if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
        this.commitPropertyFilter(this.state.propertyFilter);
      }
    };

    this.el.addEventListener(`keydown`, this.handleKeyPress);
  }

  getPropertyNameForQuery() {
    const {propertyName} = this.state.propertyFilter;

    if (Array.isArray(propertyName)) {
      return propertyName;
    }

    if (isScdPropertyName(propertyName)) {
      return propertyName.replace(SCD_PROFILE_PROPERTY_PREFIX, ``);
    }

    return propertyName;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.el.removeEventListener(`keydown`, this.handleKeyPress);
  }

  // region Property filter methods

  changePropertyFilter(newPropertyFilter) {
    this.update({propertyFilter: newPropertyFilter});

    this.dispatchEvent(
      new CustomEvent(`changePropertyFilter`, {
        detail: {
          propertyFilter: newPropertyFilter,
        },
      }),
    );
  }

  changePropertyFilterAttribute(key, value) {
    const newPropertyFilter = this.state.propertyFilter.setAttribute(key, value);

    // attempt to preserve current filter value for the selected filterOperator
    if (key === `filterOperator`) {
      const {filterValue: currentFilterValue} = this.state.propertyFilter;
      const defaultFilterValue = newPropertyFilter.filterValue;
      newPropertyFilter.filterValue = currentFilterValue;

      if (newPropertyFilter.isValid() === false) {
        newPropertyFilter.filterValue = defaultFilterValue;
      }
    }

    this.changePropertyFilter(newPropertyFilter);
  }

  commitPropertyFilter(propertyFilter) {
    if (propertyFilter.isValid()) {
      this.dispatchEvent(
        new CustomEvent(`commitPropertyFilter`, {
          detail: {
            propertyFilter,
          },
        }),
      );
    }
  }

  selectItems(items) {
    const filterValue = new Set(this.state.propertyFilter.filterValue as string[]);

    items.forEach((item) => {
      // when a user specifies a value that is not among the provided property values, it is captured in item.value
      const value = Object.prototype.hasOwnProperty.call(item, `propertyValue`) ? item.propertyValue : item.value;
      if (item.isSelected) {
        filterValue.add(value);
      } else {
        filterValue.delete(value);
      }

      if (!Object.prototype.hasOwnProperty.call(item, `propertyValue`)) {
        this.update({
          currentValues: [...(this.state.currentValues || []), item.value],
          propertyValues: [...(this.state.propertyValues || []), item.value],
        });
      }
    });

    this.helpers.changeFilterValue([...filterValue]);
  }

  // endregion

  get projectId() {
    return this.attr(`project-id`);
  }

  get workspaceId() {
    return this.attr(`workspace-id`);
  }

  get isExperimentsPropertyFilter() {
    const {propertyName, propertyType, joinPropertyType} = this.state.propertyFilter;

    return (
      (propertyType === PropertyType.Object || joinPropertyType === PropertyType.Object) &&
      propertyName === MPProperties.Experiments
    );
  }

  get isDatasetPropertyFilter() {
    const {propertyName} = this.state.propertyFilter;
    return propertyName === DATASET_PROPERTY_NAME;
  }

  // region Set up / data fetching

  updateState() {
    const propertyFilterAttrs = this.attr(`property-filter`);
    const displayContext = this.helpers.getDisplayContext();
    const propertyFilter = PropertyFilterFactory.createPropertyFilter(propertyFilterAttrs, {displayContext});

    this.update({propertyFilter});
  }

  // TODO opslead
  async fetchPropertyValues() {
    if (!this.state.propertyFilter) {
      return; // not ready to query yet
    }

    this.update({loadingPropertyValues: true});

    if (this.projectId) {
      await syncReportPropertyValueMappings(this.projectId);
    }

    const {filterValue, propertyType, joinPropertyType, propertyName, resourceType, filterDateUnit} =
      this.state.propertyFilter;
    const isString = propertyType === PropertyType.String;
    const isObject = propertyType === PropertyType.Object;
    const currentValues = Array.isArray(filterValue) && (isString || isObject) ? filterValue : [];

    // ensure that selected values are passed into checkbox component so that they may be unselected
    const appendCurrentValues = (values) =>
      uniq([...values, ...(propertyType === PropertyType.String ? currentValues : [])]);

    const isEventQuery = resourceType === ResourceType.Event || this.state.propertyFilter.scdEvent;

    let propertyValues = [];
    try {
      if (this.isDatasetPropertyFilter) {
        propertyValues = Object.keys(this.getDatasets());
      } else if (propertyName === RECURRING_REVENUE_CHANGE_TYPE.value) {
        propertyValues = REVENUE_CHANGE_TYPES.map((item) => item.value);
      } else if (filterDateUnit === SpecialTimeUnit.DayOfWeek) {
        propertyValues = DAYS_OF_THE_WEEK;
      } else if (filterDateUnit === SpecialTimeUnit.HourOfDay) {
        propertyValues = HOURS_OF_DAY;
      } else if (isEventQuery) {
        propertyValues = await this.fetchEventPropertyValues();
        propertyValues = appendCurrentValues(propertyValues);
      } else if (resourceType === ResourceType.User) {
        propertyValues = await this.fetchPeoplePropertyValues();
        propertyValues = appendCurrentValues(propertyValues);
      } else if (resourceType === ResourceType.Other && propertyName === FLOWS_CONVERSION.value) {
        // TODO: consider adding a new property values endpoint for ResourceType.Other if more such properties are added
        propertyValues = FLOWS_CONVERSION_VALUES;
      }
    } catch (err) {
      console.error(err);
    }

    if (
      isString &&
      (joinPropertyType === undefined || joinPropertyType === PropertyType.String) &&
      propertyName !== RECURRING_REVENUE_CHANGE_TYPE.value
    ) {
      propertyValues.push(ConstantPropertyValues.EmptyString);
    }

    const filterSelection = Array.isArray(filterValue) ? filterValue : [];
    this.update({
      currentValues,
      loadingPropertyValues: false,
      propertyValues: sortBy(
        propertyValues,
        // makes sure selected & special constant values surface to the top
        [(item) => !filterSelection.includes(item), (item) => ConstantPropertyValueSet.has(item) === false],
      ),
    });
  }

  async fetchEventPropertyValues() {
    const {
      behavior,
      customProperty,
      customPropertyId,
      dataGroupId,
      datasetId,
      joinPropertyType,
      listQuantifier,
      propertyObjectKey,
      tempDataGroupId,
      scdEvent,
    } = this.state.propertyFilter;

    const resolvedCustomProperty =
      customProperty || (customPropertyId && customPropertiesStore.value[customPropertyId]);
    const borrowedProperty = getBorrowedProperty(resolvedCustomProperty);

    let eventNames: string[];
    if (borrowedProperty) {
      eventNames = [borrowedProperty.borrowEvent.value];
    } else {
      eventNames = this.attr(`event-names`) && uniq(this.attr(`event-names`));
    }

    const listPropertyName = this.params.listProperty?.propertyName;

    let propertyName: PropertyValue;
    // If we have a behavior use that as the property name
    const initialPropertyName = this.getPropertyNameForQuery();
    if (behavior) {
      propertyName = behavior.property?.value;
    } else if (borrowedProperty) {
      propertyName = borrowedProperty.value;
    } else {
      propertyName = initialPropertyName;
    }

    // if events like `$all_events` are passed, all events should be queried
    const includeAllProperties =
      !eventNames ||
      eventNames.some((eventName: string) => {
        return [null, SPECIAL_EVENTS.ALL_EVENTS, SPECIAL_EVENTS.TOP_EVENTS, SPECIAL_EVENTS.ANY_EVENT].includes(
          eventName,
        );
      });
    const isObjectProperty = joinPropertyType === PropertyType.Object && propertyObjectKey?.length;

    const borrowedPropertyObjectKey = borrowedProperty?.propertyObjectKey;

    const includePropertyName =
      (borrowedProperty && !borrowedPropertyObjectKey) ||
      (!customProperty && !customPropertyId && !isObjectProperty && !listPropertyName);

    let customPropertyParams: Record<string, any> = {};
    if (!borrowedProperty) {
      if (customProperty) {
        customPropertyParams = {customProperty: {customProperty}};
      } else if (customPropertyId) {
        customPropertyParams = {customProperty: {customPropertyId}};
      }
    }

    const queryParams = {
      ...(dataGroupId ? {dataGroupId} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      ...(datasetId ? {datasetId} : {}),
      ...(includeAllProperties ? {} : {eventName: eventNames}),
      projectId: this.projectId,
      ...(joinPropertyType === PropertyType.Object && propertyObjectKey?.length
        ? {
            objectProperty: this.state.propertyFilter.getAttributes(),
          }
        : {}),
      ...customPropertyParams,
      ignoreListItem: !listQuantifier,
      ...(listPropertyName
        ? {
            objectProperty: {
              resourceType: ResourceType.Event,
              propertyType: `list`,
              propertyName: listPropertyName,
              propertyObjectKey: [propertyName],
            },
          }
        : {}),
      ...(borrowedPropertyObjectKey
        ? {
            objectProperty: {
              resourceType: borrowedProperty.resourceType,
              propertyName: borrowedProperty.value,
              joinPropertyType: borrowedProperty.joinPropertyType,
              propertyType: borrowedProperty.type,
              propertyObjectKey: borrowedPropertyObjectKey,
            },
          }
        : {}),
      ...(includePropertyName ? {propertyName} : {}),
      ...(Array.isArray(propertyName) ? {dataGroupPropertyName: propertyName[1]} : {}),
      ...(scdEvent ? {scdEvent} : {}),
      workspaceId: this.workspaceId,
    };
    const results = await eventPropertyValuesClient.query(queryParams);
    // TODO (egaebel): Push this logic into the query API backend instead.
    if (propertyObjectKey?.length) {
      // Extract field from object properties and flatten the results if it's a list.
      return results.flatMap((jsonObjectResult, _) => {
        try {
          const objectField = get(JSON.parse(jsonObjectResult), propertyObjectKey);
          if (!objectField) {
            // This is a temporary solution to handle boolean values typecasted as string.
            // "true" and "false" can be parsed but objectField is undefined. In this case,
            // display jsonObjectResult
            return [jsonObjectResult];
          }
          if (!(objectField instanceof Array)) {
            return [objectField];
          }
          return objectField;
        } catch (err) {
          // Display the raw invalid JSON string if it cannot be parsed. This
          // may be either an unterminated JSON string or a simple string that
          // is not supposed to be JSON at all.
          return [jsonObjectResult];
        }
      });
    }
    return results;
  }

  fetchPeoplePropertyValues() {
    const {
      customProperty,
      joinPropertyType,
      propertyObjectKey,
      listQuantifier,
      datasetId,
      dataGroupId,
      tempDataGroupId,
      scdEvent,
    } = this.state.propertyFilter;

    const propertyName = this.getPropertyNameForQuery();
    const isObjectProperty = joinPropertyType === PropertyType.Object && propertyObjectKey?.length;
    const includePropertyName = !customProperty && !isObjectProperty;
    const listProperty = this.params.listProperty;

    return peoplePropertyValuesClient.query({
      projectId: this.projectId,
      ...(customProperty ? {customProperty: {customProperty}} : {}),
      ...(includePropertyName ? {propertyName} : {}),
      ...(Array.isArray(propertyName) ? {dataGroupPropertyName: propertyName[1]} : {}),
      ...(datasetId ? {datasetId} : {}),
      ...(joinPropertyType === PropertyType.Object && propertyObjectKey?.length
        ? {
            objectProperty: this.state.propertyFilter.getAttributes(),
          }
        : {}),
      ...(listProperty
        ? {
            objectProperty: {
              resourceType: ResourceType.User,
              propertyType: `list`,
              propertyName: listProperty.propertyName,
              propertyObjectKey: [propertyName],
            },
          }
        : {}),
      ...(dataGroupId ? {dataGroupId} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      ...(scdEvent ? {scdEvent} : {}),
      ignoreListItem: !listQuantifier,
      workspaceId: this.workspaceId,
    });
  }

  getDatasets() {
    const mappings = getPropertyValueMappings() || {};

    return mappings[`datasets`] || [];
  }

  /**
   * For object property types, parse their JSON values
   * TODO(pablo): The API returns malformed blobs in some instances, to
   * avoid failing the whole selection menu, catch those errors and
   * display partial results
   */
  getObjectPropertyValues() {
    const propertyValues = this.state.propertyValues || [];

    return propertyValues
      .map((value) => {
        try {
          return JSON.parse(value);
        } catch (err) {
          console.warn(`Error parsing object property values`, err, value);
          return {};
        }
      })
      .filter((x) => x);
  }

  getPropertyObjectKeys() {
    const {propertyObjectKey} = this.state.propertyFilter;
    const propertyValues = this.getObjectPropertyValues();

    const keys = new Set(propertyObjectKey ? [propertyObjectKey as string] : []);
    propertyValues.forEach((value) => Object.keys(value).forEach((key) => keys.add(key)));

    return Array.from(keys);
  }

  /**
   * Returns a list of section items that belong
   * to a given object entry, or variants that
   * belong to a given experiment
   */
  getPropertyObjectKeyValues(selectedKey) {
    const {currentValues} = this.state;
    const propertyValues = this.getObjectPropertyValues();

    // displays only plain values to avoid persisting
    // string representations of object-like values
    const values = [
      // append existing unkown values so they render in the menu
      ...currentValues,
      // map all values for the selected key from the loaded prop values
      ...propertyValues.map((value) => value[selectedKey]),
    ].filter((value) => isNumber(value) || isString(value) || isBoolean(value));

    return [
      {label: `Any value`, value: null},
      ...Array.from(new Set(values)).map((value) => ({label: value.toString(), value})),
    ];
  }

  shouldUpdate(stateUpdate) {
    const state = {...this.state, ...stateUpdate};
    return state.propertyFilter && state.propertyFilter.propertyName && state.propertyFilter.propertyType;
  }

  enhancedListFilterEnabled() {
    return isEnhancedListFilter(this.state.propertyFilter) && this.attr(`enable-enhanced-list-filters`);
  }

  // endregion
}

type JsxProps = {
  attrs?: TODO;
  params?: PropertyValuesScreenParams;
};

export const PropertyValuesScreenEl = defineAndMakeJsxFactory<JsxProps>(
  `mp-property-values-screen`,
  PropertyValuesScreen,
);
export const PropertyValuesScreenReact = panel2react<JsxProps>(`mp-property-values-screen`, PropertyValuesScreen);
